import moment from "moment";
import { RESERVATION_TYPE, normalizeType } from "./reservationViewUtils";

export const PHYSICAL_ROOM_STATUSES = {
  available: "available",
  occupied: "occupied",
  blocked: "blocked",
};

/** Only confirmed ("Reservation") and checked-in stays occupy a physical room. */
export const reservationOccupiesPhysicalRoom = (res) => {
  const type = normalizeType(res?.reservationType);
  return (
    type === RESERVATION_TYPE.CONFIRMED ||
    type === RESERVATION_TYPE.CHECKED_IN
  );
};

/** Assigned units held for staff assignment (unconfirmed) plus occupied stays. */
export const reservationHoldsPhysicalRoomAssignment = (res) => {
  const type = normalizeType(res?.reservationType);
  return (
    type === RESERVATION_TYPE.UNCONFIRMED ||
    type === RESERVATION_TYPE.CONFIRMED ||
    type === RESERVATION_TYPE.CHECKED_IN
  );
};

/** Stay nights: check-in inclusive, check-out exclusive. */
export const stayNights = (checkIn, checkOut) => {
  const s = moment(checkIn, "YYYY-MM-DD", true);
  const e = moment(checkOut, "YYYY-MM-DD", true);
  if (!s.isValid() || !e.isValid() || !e.isAfter(s, "day")) return [];
  const nights = [];
  const cursor = s.clone();
  while (cursor.isBefore(e, "day")) {
    nights.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "day");
  }
  return nights;
};

export const isDateInBlockRange = (date, block) => {
  const d = moment(date, "YYYY-MM-DD", true);
  const start = moment(block?.startDate, "YYYY-MM-DD", true);
  const end = moment(block?.endDate, "YYYY-MM-DD", true);
  if (!d.isValid() || !start.isValid() || !end.isValid()) return false;
  return d.isSameOrAfter(start, "day") && d.isSameOrBefore(end, "day");
};

export const getAssignedPhysicalIds = (res) =>
  (res.roomDetails || [])
    .flatMap((detail) =>
      (detail.physicalRooms || []).map((p) => p?.id || p).filter(Boolean)
    )
    .map(String);

export const buildOccupiedByDate = (
  reservations,
  {
    hotelId,
    nights,
    excludeReservationId = "",
    occupiesFn = reservationOccupiesPhysicalRoom,
  }
) => {
  const occupancy = new Map();
  nights.forEach((d) => occupancy.set(d, new Set()));
  if (!nights.length) return occupancy;

  const rangeStart = moment(nights[0], "YYYY-MM-DD");
  const rangeEnd = moment(nights[nights.length - 1], "YYYY-MM-DD");

  (reservations || []).forEach((res) => {
    if (String(res.hotelId || "") !== String(hotelId || "")) return;
    if (
      excludeReservationId &&
      String(res.id) === String(excludeReservationId)
    ) {
      return;
    }
    if (!occupiesFn(res)) return;

    const checkIn = moment(res.checkIn, "YYYY-MM-DD");
    const checkOut = moment(res.checkOut, "YYYY-MM-DD");
    if (!checkIn.isValid() || !checkOut.isValid()) return;
    if (
      checkOut.isSameOrBefore(rangeStart, "day") ||
      checkIn.isAfter(rangeEnd, "day")
    ) {
      return;
    }

    const physicalIds = getAssignedPhysicalIds(res);
    if (!physicalIds.length) return;

    nights.forEach((d) => {
      const day = moment(d, "YYYY-MM-DD");
      const inStay =
        day.isSameOrAfter(checkIn, "day") && day.isBefore(checkOut, "day");
      if (!inStay) return;
      physicalIds.forEach((id) => occupancy.get(d).add(String(id)));
    });
  });

  return occupancy;
};

export const isPhysicalRoomFreeOnDate = (unit, date, occupiedByDate) => {
  if (unit.status === PHYSICAL_ROOM_STATUSES.blocked) return false;
  if ((unit.blocks || []).some((block) => isDateInBlockRange(date, block))) {
    return false;
  }
  if (occupiedByDate.get(date)?.has(String(unit.id))) return false;
  return true;
};

/** True only if the unit is free on every stay night (no split-stay assignment). */
export const isPhysicalRoomAvailableForStay = (
  unit,
  nights,
  occupiedByDate
) => {
  if (!nights.length) return false;
  return nights.every((date) =>
    isPhysicalRoomFreeOnDate(unit, date, occupiedByDate)
  );
};

/** Normalize stored assignments (array, single object, or id strings). */
export const normalizePhysicalRoomAssignments = (room) => {
  const raw = Array.isArray(room?.physicalRooms)
    ? room.physicalRooms
    : room?.physicalRooms
    ? [room.physicalRooms]
    : Array.isArray(room?.assignedPhysicalRooms)
    ? room.assignedPhysicalRooms
    : [];

  return raw
    .map((unit) => {
      if (unit == null || unit === "") return null;
      if (typeof unit === "object") {
        const id = unit.id || unit.physicalRoomId || "";
        const label = unit.label || unit.name || "";
        if (!id && !label) return null;
        return { id: String(id || label), label: String(label || "") };
      }
      return { id: String(unit), label: "" };
    })
    .filter(Boolean);
};

export const formatAssignedPhysicalRooms = (room, labelById = {}) => {
  const labels = normalizePhysicalRoomAssignments(room)
    .map((unit) => labelById[unit.id] || unit.label || unit.id)
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "-";
};
